/**
 * SalesAccounts — Slice A (Phase 1 / 1.1) Accounts list.
 *
 * Read path: GET /v1/sales/accounts (A1) — name, #contacts, #open deals, open
 * value, ALL server-aggregated (listAccountsSQL is the source of every number
 * here; nothing is summed client-side), plus Accounts S2's per-account facts
 * (furthest open stage, the newest COMPLETED activity, the bare domain, R1's
 * per-deal verdicts counted per account, linked open leads, duplicate
 * suggestions). Search filters the fetched list locally (≤ the server's
 * 200-row page — the honest "Showing X of Y" line says so).
 *
 * Money: open value is the server's FENCED GBP-comparable sum (currency-fence
 * increment 0: GBP rows as themselves, captured non-GBP rows by their base)
 * rendered as GBP; when the server excluded uncaptured non-GBP deals the row
 * SAYS so instead of silently under-counting.
 *
 * Duplicates are their own read, GET /v1/sales/accounts/duplicates (ruling of
 * 2026-10-25), fetched beside the list and attached per row.
 *
 * Accounts S3 (accounts-spec.md §3.2–§3.4, RULED 2026-10-24; accountsView.ts
 * holds every rule): the columns Account · Contacts · Open pipeline · Last
 * activity · Needs attention · ⋯ (Open, Edit, and S6's Merge with…),
 * the Needs attention view, the Last activity sort, the Account health card
 * (Fix all — S7, below), Tidy for a lower-case name through the existing PATCH
 * (the server writes its history row), and Add stakeholders / Add people
 * opening New contact with the account pre-set. Every write re-reads the list
 * (silently): the domain, the facts and the counts are the server's, never a
 * client copy of its rules.
 *
 * Accounts S6 (rulings of 2026-10-24 and 2026-10-27): "Merge with…" in the ⋯
 * menu and a Merge action on the duplicate pill open AccountMergeDialog — a
 * preview, a per-merge confirm, then the merged toast with Undo. Nothing
 * merges without that confirm. Ruling 1: owner/admin only (the server's
 * named 403 is the backstop) — `canMerge` comes from /v1/me and fails
 * closed; everyone else sees "Ask an admin to merge" by the pill and no
 * merge control at all.
 *
 * Accounts S7 (ruling of 2026-10-27): the Clean-ups tile's "Fix all ›" opens
 * FixAllSheet — every suggested tidy and merge with what each would do. The
 * tidies apply as one batch with one Undo; each selected merge then opens
 * AccountMergeDialog in turn — its own confirm, its own merged toast with
 * Undo (a Cancel skips that one). Merging stays owner/admin only, and since
 * §44 (ruling of 2026-10-30) so does Fix all itself: the link and the sheet
 * are records-level, like Tidy (the tidy batch is an account edit).
 *
 * The region and sector chips (S4 follow-up, ruling of 2026-10-27):
 * "Region: Any ▾" and "Sector: Any ▾" beside Sort, each shown once at least
 * one account carries a code (the summary's per-option counts, which the
 * menu shows). The server filters before its page limit, like the views;
 * while a chip filters, the view counts — tenant-wide — drop their number,
 * and the footer's total is the chosen code's count only when it is the one
 * filter on All (otherwise no total is claimed).
 *
 * Visual (docs/design/stier/ADOPTION.md): the Contacts page treatment
 * verbatim — SectionTitle, .k-card + .sales-table (§8.4: one
 * .ops-table/.sales-table definition), the honest "Showing X of Y" line (§8.4:
 * no dead pagination controls), the k-btn family, the search input on the
 * control radius (§8.4 REFUSED list). New account opens NewAccountPopup
 * (Accounts S8b), which wears .k-drawer (§8.4 portals). No new tokens.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Search, Globe, MoreHorizontal, Pencil, ArrowRight, Users, Sparkles, Check, GitMerge } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesAccountRow } from '../../types'
import { fetchAccounts, fetchAccountDuplicates, fetchAccountsSummary, updateAccount } from '../../api'
import { compactMoney, withNonGbpNote } from '../../../../lib/money'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { useSavedViews } from './useSavedViews'
import { ACCOUNTS_SCHEMA } from './savedViews'
import { SavedViewsBar } from './SavedViewsBar'
import { refreshSalesFigures } from '../../salesFigures'
import { InlinePicklist } from './InlinePicklist'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { AccountWebsitesDrawer, type WebsiteTarget } from './AccountWebsitesDrawer'
import { FixAllSheet } from './FixAllSheet'
import { mergedOf, survivorOf } from './accountCleanups'
import { regionLabel, sectorLabel } from '../../../../lib/accountCodes'
import { NewContactDrawer } from './SalesContacts'
import { NewAccountPopup } from './NewAccountPopup'
import { accent } from '../../../../theme/tokens'
import type { AccountPick } from './AccountPicker'
import { AccountMergeDialog, type MergeAccountRef } from './AccountMerge'
import { announceMerge } from './accountMergeActions'
import {
  accountHealth, attentionFor, isUntidyName, lastActivityLabel, londonToday, needsAttention, pageCapNote, pipelineLine, summaryStatsLine,
  tidyName, withDuplicates,
} from './accountsView'
import type { AccountMergeSuggestion, SalesAccountsSummary } from '../../../../api/types'

/** The list read's page: the first LIST_LIMIT accounts by name. */
const LIST_LIMIT = 200

// Accounts S1 + S3 (spec §3.1/§3.3; RULED 2026-10-24) — the four views, each
// counted from the list wire, and the four sorts. Views and sort are page
// state, not saved-view state: ACCOUNTS_SCHEMA stays columns-only.
type AccountsView = 'all' | 'open' | 'attention' | 'noContacts'
// `server`: the list read's own view (S2, rulings of 2026-10-25) — filtered
// before the page limit. Needs attention has none: the page filters its loaded
// rows (the declared measurement put a server view over its bar). `test` runs
// on every view, so an api that ignores `view` still shows the right rows.
const VIEWS: { id: AccountsView; label: string; test: (a: SalesAccountRow) => boolean; server?: 'with_open_deals' | 'no_contacts' }[] = [
  { id: 'all',        label: 'All',             test: () => true },
  { id: 'open',       label: 'With open deals', test: a => a.openDealCount > 0, server: 'with_open_deals' },
  { id: 'attention',  label: 'Needs attention', test: needsAttention },
  { id: 'noContacts', label: 'No contacts',     test: a => a.contactCount === 0, server: 'no_contacts' },
]
type AccountsSort = 'openValue' | 'lastActivity' | 'name' | 'contacts'
const SORTS: { id: AccountsSort; label: string; footer: string }[] = [
  { id: 'openValue',    label: 'Open value',    footer: 'sorted by open value' },
  { id: 'lastActivity', label: 'Last activity', footer: 'sorted by last activity' },
  { id: 'name',         label: 'Name',          footer: 'sorted by name' },
  { id: 'contacts',     label: 'Contacts',      footer: 'sorted by contacts' },
]
const byName = (a: SalesAccountRow, b: SalesAccountRow) => a.name.localeCompare(b.name)
const completedAt = (a: SalesAccountRow) => a.facts?.lastCompletedActivityAt ?? ''
function compareAccounts(sort: AccountsSort): (a: SalesAccountRow, b: SalesAccountRow) => number {
  if (sort === 'openValue') return (a, b) => b.openValuePence - a.openValuePence || byName(a, b)
  if (sort === 'contacts') return (a, b) => b.contactCount - a.contactCount || byName(a, b)
  // Newest completed first; never-completed last (ISO strings sort as time).
  if (sort === 'lastActivity') return (a, b) => completedAt(b).localeCompare(completedAt(a)) || byName(a, b)
  return byName
}

/** The stats line's third figure (spec §3.1): the fenced open value summed
 *  over the (up to) three accounts holding the most. Absent when no account
 *  has open value; the non-GBP exclusion is named, never hidden. */
function largestClause(accounts: SalesAccountRow[]): string {
  const top = accounts.filter(a => a.openValuePence > 0)
    .sort((a, b) => b.openValuePence - a.openValuePence).slice(0, 3)
  if (top.length === 0) return ''
  const sum = top.reduce((s, a) => s + a.openValuePence, 0)
  const excluded = top.reduce((s, a) => s + a.openValueExcluded, 0)
  const base = `${compactMoney(sum, 'GBP')} open on the ${top.length === 1 ? 'largest' : `${top.length} largest`}`
  return withNonGbpNote(base, excluded, 'deal')
}

interface RowActions {
  onAddWebsite: (a: SalesAccountRow) => void
  onTidy: (a: SalesAccountRow) => void
  onAddPeople: (a: SalesAccountRow) => void
  onMerge: (a: SalesAccountRow, other?: MergeAccountRef) => void
  /** S6 ruling 1: the session may merge (owner or admin; fail closed). */
  canMerge: boolean
  /** Records access (lib/writeAccess): Add website, Tidy and Add people are
   *  account / contact writes — hidden without it. */
  canEdit: boolean
  tidying: string | null
  today: string
}

/** Body cell per ACCOUNTS_SCHEMA column id (columns-only list, D2). */
function accountCell(a: SalesAccountRow, act: RowActions): Record<string, React.ReactNode> {
  const f = a.facts
  // The bare domain is the server's (S2 AccountDomainSQL); the user's own
  // value stays in the Website column.
  const domain = f ? f.domain : a.website
  // Accounts S4: region and sector join the sub-line, set values only.
  const coded = [regionLabel(a.region), sectorLabel(a.sector)].filter(Boolean)
  const pill = attentionFor(a)
  return {
    account: (
      <div className="flex items-center gap-3">
        <Avatar name={a.name} size={28} />
        <div className="min-w-0">
          <div className="text-13 font-semibold text-ink-primary truncate">{a.name}</div>
          {domain
            ? <div className="text-11 text-ink-muted truncate">{[domain, ...coded].join(' · ')}</div>
            : coded.length === 0
              // No codes: exactly the S3 markup (the wrapper below would grow the row).
              ? (act.canEdit && <button type="button" className="k-accounts-add" onClick={e => { e.stopPropagation(); act.onAddWebsite(a) }}>Add website</button>)
              : act.canEdit ? (
                <div className="text-11 text-ink-muted truncate">
                  <button type="button" className="k-accounts-add" onClick={e => { e.stopPropagation(); act.onAddWebsite(a) }}>Add website</button>
                  <span>{` · ${coded.join(' · ')}`}</span>
                </div>
              ) : <div className="text-11 text-ink-muted truncate">{coded.join(' · ')}</div>}
        </div>
      </div>
    ),
    website: <span className="text-12 text-ink-secondary">{a.website || '—'}</span>,
    region: a.region
      ? <span className="text-12 text-ink-secondary">{regionLabel(a.region)}</span>
      : <span className="text-12 text-ink-disabled">Not set</span>,
    sector: a.sector
      ? <span className="text-12 text-ink-secondary">{sectorLabel(a.sector)}</span>
      : <span className="text-12 text-ink-disabled">Not set</span>,
    contacts: a.contactCount > 0
      ? <span className="text-12 font-semibold text-ink-body">{a.contactCount}</span>
      : <span className="text-12 text-ink-disabled">None</span>,
    openDeals: <span className={`text-12 font-semibold ${a.openDealCount > 0 ? 'text-accent' : 'text-ink-disabled'}`}>{a.openDealCount}</span>,
    openValue: a.openDealCount > 0 ? (
      <>
        <div className="text-13 font-semibold text-ink-primary">{compactMoney(a.openValuePence, 'GBP')}</div>
        <div className="text-11 text-ink-muted">{pipelineLine(a.openDealCount, f?.furthestOpenStage ?? '')}</div>
        {a.openValueExcluded > 0 && (
          <div className="text-11 text-ink-muted">
            {a.openValueExcluded} unconverted deal{a.openValueExcluded === 1 ? '' : 's'} excluded
          </div>
        )}
      </>
    ) : <span className="text-12 text-ink-disabled">No open deals</span>,
    lastActivity: f
      ? (f.lastCompletedActivityAt
        ? <span className="text-12 text-ink-body">{lastActivityLabel(f.lastCompletedActivityAt, act.today)}</span>
        : <span className="text-12 text-ink-disabled">Never</span>)
      : <span className="text-12 text-ink-muted">{a.lastActivity}</span>,
    attention: pill === undefined ? null : pill === null ? (
      <span className="k-accounts-pill k-accounts-pill--ok"><Check size={12} aria-hidden="true" /> Looks healthy</span>
    ) : (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`k-accounts-pill k-accounts-pill--${pill.tone}`}>{pill.text}</span>
        {pill.detail && <span className="text-11 text-ink-muted">{pill.detail}</span>}
        {pill.kind === 'untidy' && act.canEdit && (
          <button type="button" className="k-accounts-pill-action" disabled={act.tidying === a.id}
            aria-label={`Tidy ${a.name} to ${tidyName(a.name)}`}
            onClick={e => { e.stopPropagation(); act.onTidy(a) }}>
            {act.tidying === a.id ? 'Tidying…' : 'Tidy'}
          </button>
        )}
        {pill.kind === 'duplicate' && a.facts?.duplicates?.[0] && !act.canMerge && (
          <span className="text-11 text-ink-muted">Ask an admin to merge</span>
        )}
        {pill.kind === 'duplicate' && a.facts?.duplicates?.[0] && act.canMerge && (
          <button type="button" className="k-accounts-pill-action"
            aria-label={`Merge ${a.name} with ${a.facts.duplicates[0].name}`}
            onClick={e => { e.stopPropagation(); act.onMerge(a, { id: a.facts!.duplicates![0].accountId, name: a.facts!.duplicates![0].name }) }}>
            Merge
          </button>
        )}
        {pill.kind === 'singleThreaded' && act.canEdit && (
          <button type="button" className="k-accounts-pill-action" aria-label={`Add people to ${a.name}`}
            onClick={e => { e.stopPropagation(); act.onAddPeople(a) }}>
            Add people
          </button>
        )}
      </div>
    ),
  }
}

/** The row's ⋯ menu (RULED 2026-10-24: Open, Edit; S6 adds Merge with…).
 *  Portalled and fixed, as InlinePicklist is, because the list card clips
 *  overflow; Escape, a click away, a scroll or a resize closes it. */
function AccountRowMenu({ name, onOpen, onEdit, onMerge }: { name: string; onOpen: () => void; onEdit?: () => void; onMerge?: () => void }) {
  const [at, setAt] = useState<{ top: number; right: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setAt(null), [])
  useLayoutEffect(() => {
    if (!at) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(); btn.current?.focus() } }
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [at, close])
  const toggle = () => {
    if (at) { close(); return }
    const r = btn.current!.getBoundingClientRect()
    setAt({ top: r.bottom + 4, right: window.innerWidth - r.right })
  }
  return (
    <>
      <button ref={btn} type="button" className="k-contacts-kebab" aria-label={`More actions for ${name}`}
        aria-haspopup="menu" aria-expanded={at !== null} onClick={e => { e.stopPropagation(); toggle() }}>
        <MoreHorizontal size={16} />
      </button>
      {at && createPortal(
        <>
          <div className="fixed inset-0" style={{ zIndex: 60 }} onClick={close} />
          <div role="menu" aria-label={`Actions for ${name}`} className="k-menu"
            style={{ position: 'fixed', top: at.top, right: at.right, left: 'auto', minWidth: 160, zIndex: 61 }}>
            <button type="button" role="menuitem" className="k-menuitem" onClick={() => { close(); onOpen() }}>
              <ArrowRight size={14} aria-hidden="true" /> Open
            </button>
            {onEdit && (
              <button type="button" role="menuitem" className="k-menuitem" onClick={() => { close(); onEdit() }}>
                <Pencil size={14} aria-hidden="true" /> Edit
              </button>
            )}
            {onMerge && (
              <button type="button" role="menuitem" className="k-menuitem" onClick={() => { close(); onMerge() }}>
                <GitMerge size={14} aria-hidden="true" /> Merge with…
              </button>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  )
}

function HealthTile({ icon, title, body, action }: { icon: React.ReactNode; title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="k-accounts-tile">
      <span className="k-accounts-tile-icon" aria-hidden="true">{icon}</span>
      <div className="min-w-0 flex flex-col gap-1">
        <div className="k-accounts-tile-title">{title}</div>
        <p className="k-accounts-tile-body">{body}</p>
        {action}
      </div>
    </div>
  )
}

/** The list read with a SILENT re-read: the first load shows the loading
 *  state; a reload after a write keeps the rows on screen and swaps them when
 *  the server answers (a failed reload keeps the last good rows and says so).
 *  The duplicates and the tenant-wide summary are their own reads, in parallel
 *  (rulings of 2026-10-25): a failure of either — or an api without them —
 *  leaves them UNKNOWN (null), never the page down and never a zero. */
function useAccountsList(serverView: 'with_open_deals' | 'no_contacts' | undefined, region: string, sector: string) {
  const [state, setState] = useState<{ data: SalesAccountRow[] | null; summary: SalesAccountsSummary | null; error: string | null }>({ data: null, summary: null, error: null })
  const [nonce, setNonce] = useState(0)
  useEffect(() => {
    let alive = true
    const filter = region || sector ? [{ region, sector }] : []
    Promise.all([fetchAccounts(undefined, LIST_LIMIT, serverView, ...filter), fetchAccountDuplicates().catch(() => null), fetchAccountsSummary().catch(() => null)])
      .then(([rows, pairs, summary]) => { if (alive) setState({ data: withDuplicates(rows, pairs), summary, error: null }) })
      .catch(e => {
        if (!alive) return
        const message = apiErrorMessage(e, 'Failed to load')
        setState(s => {
          if (s.data) toast.error(message)
          return { data: s.data, summary: s.summary, error: s.data ? null : message }
        })
      })
    return () => { alive = false }
  }, [nonce, serverView, region, sector])
  const reload = useCallback(() => setNonce(n => n + 1), [])
  return { ...state, loading: state.data === null && state.error === null, reload }
}

// ── List page ─────────────────────────────────────────────────────────────

/** How long a just-saved row keeps its highlight. */
const HIGHLIGHT_MS = 4000

export function SalesAccounts({ onSelectAccount, newAccountOpen = false, onNewAccountClose, canMerge = false }: {
  /** Open an account; `edit` opens its page with the header edit form open
   *  (the ⋯ menu's Edit). */
  onSelectAccount: (id: string, opts?: { edit?: boolean }) => void
  /** S1: the New Account drawer's open state when opened from the shell's
   *  primary-action slot (Dashboard.tsx holds it, as for Contacts). */
  newAccountOpen?: boolean
  onNewAccountClose?: () => void
  /** S6 ruling 1: owner or admin per /v1/me; false hides every merge control. */
  canMerge?: boolean
}) {
  const [view, setView]         = useState<AccountsView>('all')
  // The chips' codes; '' = Any.
  const [region, setRegion]     = useState('')
  const [sector, setSector]     = useState('')
  const { data, summary, loading, error, reload } = useAccountsList(VIEWS.find(v => v.id === view)?.server, region, sector)
  const [search, setSearch]     = useState('')
  const [sort, setSort]         = useState<AccountsSort>('openValue')
  const [websiteTargets, setWebsiteTargets] = useState<WebsiteTarget[] | null>(null)
  const [stakeholdersFor, setStakeholdersFor] = useState<AccountPick | null>(null)
  // S8b (spec 2 §3b.5): the row a New account save created or renamed wears a
  // highlight for a few seconds after the list re-reads.
  const [highlightId, setHighlightId] = useState<string | null>(null)
  useEffect(() => {
    if (!highlightId) return
    const t = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS)
    return () => clearTimeout(t)
  }, [highlightId])
  const [tidying, setTidying]   = useState<string | null>(null)
  const [mergeFor, setMergeFor] = useState<{ source: MergeAccountRef; target?: MergeAccountRef } | null>(null)
  // S7: Fix all's sheet, and the merges it handed over, confirmed one by one.
  const [fixAllOpen, setFixAllOpen] = useState(false)
  const [mergeQueue, setMergeQueue] = useState<AccountMergeSuggestion[]>([])
  // Columns-only saved views (D2): no filters, no sort — search stays local.
  const sv = useSavedViews(ACCOUNTS_SCHEMA)
  // Account create / edit / tidy, websites and stakeholders are records-level
  // writes (lib/writeAccess; fail closed). Merge keeps its own canMerge.
  const { records } = useWriteAccess()

  if (loading) return <LoadingState />
  if (error || !data) return <ErrorState message={error ?? 'Failed to load'} />

  const accounts = data
  const today = londonToday()
  // The S2 facts are on the wire (mapAccountRow sets them only when the api
  // serves them): without them there is no honest health card and no Needs
  // attention view to count, so neither renders.
  // An empty server view has no rows to carry facts; the summary only exists
  // on an S2 api, so either one says the facts are served.
  const hasFacts = summary !== null || accounts.some(a => a.facts)
  const views = hasFacts ? VIEWS : VIEWS.filter(v => v.id !== 'attention')
  // The view counts and the stats line are TENANT-WIDE: the summary's (ruling
  // of 2026-10-25). Without the S2 facts (an older api) they stay S1's, counted
  // from the rows; with the facts but no summary they are unavailable — the
  // labels lose their number, never show a zero.
  const chipFiltered = region || sector
  const viewCount = (v: typeof VIEWS[number]): number | null =>
    chipFiltered ? null
    : !hasFacts ? accounts.filter(v.test).length
    : summary === null ? null
    : { all: summary.accounts, open: summary.withOpenDeals, attention: summary.needsAttention, noContacts: summary.noContacts }[v.id]
  // One chip on All: its count is the total; any other combination has no
  // tenant-wide figure, so no total is claimed.
  const chipTotal = (): number | null => {
    if (view !== 'all' || (region && sector)) return null
    const list = region ? summary?.regions : summary?.sectors
    return list?.find(c => c.code === (region || sector))?.accounts ?? null
  }
  const total = chipFiltered ? chipTotal() : hasFacts ? (summary?.accounts ?? null) : accounts.length
  // The listed rows are the first LIST_LIMIT accounts by name of the view's
  // read (the server's view, or the plain page that Needs attention filters):
  // when the view's total is more, the footer says so (the cap-sentence
  // family). The total is the summary's; without it, a full page says so too.
  const viewTotal = chipFiltered ? chipTotal() : summary === null ? null
    : { all: summary.accounts, open: summary.withOpenDeals, attention: summary.accounts, noContacts: summary.noContacts }[view]
  const capNote = viewTotal !== null ? pageCapNote(accounts.length, viewTotal)
    : accounts.length >= LIST_LIMIT ? pageCapNote(accounts.length, Number.POSITIVE_INFINITY) : null

  const q = search.trim().toLowerCase()
  const viewTest = (views.find(v => v.id === view) ?? views[0]).test
  const filtered = accounts
    .filter(viewTest)
    .filter(a => !q || a.name.toLowerCase().includes(q) || a.website.toLowerCase().includes(q) || (a.facts?.domain ?? '').includes(q))
    .sort(compareAccounts(sort))
  const withOpen = accounts.filter(a => a.openDealCount > 0).length
  const largest = largestClause(accounts)
  const statsLine = !hasFacts ? `${accounts.length} accounts · ${withOpen} with open deals${largest ? ` · ${largest}` : ''}`
    : summary ? summaryStatsLine(summary) : 'Account figures are not available right now.'
  const health = summary ? accountHealth(summary) : null
  // Find websites opens the form on the LISTED accounts without one.
  const pageMissing = accounts.filter(a => !a.website)
  const openWebsites = (list: SalesAccountRow[]) => setWebsiteTargets(list.map(a => ({ id: a.id, name: a.name })))
  const addPeople = (a: SalesAccountRow) => setStakeholdersFor({ accountId: a.id, company: a.name })
  const visibleCols = new Set(sv.state.columns)
  const shownColumns = ACCOUNTS_SCHEMA.columns.filter(c => visibleCols.has(c.id) && (hasFacts || c.id !== 'attention'))

  async function tidy(a: SalesAccountRow) {
    if (!isUntidyName(a.name)) return
    setTidying(a.id)
    try {
      // The existing PATCH: the server records the name change in the
      // account's field history, actor threaded.
      const next = await updateAccount(a.id, { name: tidyName(a.name) })
      toast.success(`Renamed to "${next.name}"`)
      reload()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to rename account'))
    } finally {
      setTidying(null)
    }
  }

  const openMerge = (a: SalesAccountRow, other?: MergeAccountRef) => setMergeFor({ source: { id: a.id, name: a.name }, target: other })
  const afterMerge = () => { void refreshSalesFigures(); reload() }
  const actions: RowActions = { onAddWebsite: a => openWebsites([a]), onTidy: a => { void tidy(a) }, onAddPeople: addPeople, onMerge: openMerge, canMerge, canEdit: records, tidying, today }

  return (
    <div className="page-enter k-accounts flex flex-col gap-4">
      {/* New account is the shell's slot button (Dashboard.tsx, S1). */}
      <SectionTitle
        title="Accounts"
        subtitle={statsLine}
      />

      {/* Account health (S3, spec §3.2 as ruled): the headline by rule and
          the three tiles over the TENANT-WIDE summary; Clean-ups carries Fix all (S7). The
          summary unavailable: the card says so — no tile, no zero. */}
      {hasFacts && <section className="k-card k-accounts-health" aria-label="Account health">
        <span className="k-accounts-health-chip"><Sparkles size={12} aria-hidden="true" /> Account health</span>
        {health ? (
          <>
            <h2 className="k-accounts-health-head">{health.headline}</h2>
            <div className="k-accounts-tiles">
              <HealthTile icon={<Users size={16} />} title="Single-threaded" body={health.single.body}
                action={records && health.single.top && (
                  <button type="button" className="k-accounts-tile-link"
                    onClick={() => setStakeholdersFor({ accountId: health.single.top!.accountId, company: health.single.top!.name })}>
                    Add stakeholders <span aria-hidden="true">›</span>
                  </button>
                )} />
              <HealthTile icon={<Sparkles size={16} />} title="Clean-ups" body={health.cleanups.body}
                action={records && summary !== null && summary.cleanups.messyAccounts > 0 && (
                  <button type="button" className="k-accounts-tile-link" onClick={() => setFixAllOpen(true)}>Fix all <span aria-hidden="true">›</span></button>
                )} />
              <HealthTile icon={<Globe size={16} />} title="Missing websites" body={health.websites.body}
                action={records && health.websites.missing > 0 && pageMissing.length > 0 && (
                  <button type="button" className="k-accounts-tile-link" onClick={() => openWebsites(pageMissing)}>Find websites <span aria-hidden="true">›</span></button>
                )} />
            </div>
          </>
        ) : (
          <p className="k-accounts-tile-body">Account health is not available right now.</p>
        )}
      </section>}

      {/* Saved views + column picker (Slice D / D2 — columns only) */}
      <SavedViewsBar sv={sv} schema={ACCOUNTS_SCHEMA} baseLabel="All accounts" />

      {/* Controls (S1 + S3): the view segmented control with its counts, the
          sort chip, the page's search. */}
      <div className="flex items-center gap-[10px] flex-wrap">
        <div role="group" aria-label="View" className="k-seg">
          {views.map(v => (
            <button key={v.id} type="button" aria-pressed={view === v.id} onClick={() => setView(v.id)}>
              {v.label}{viewCount(v) !== null && <> <span>{viewCount(v)}</span></>}
            </button>
          ))}
        </div>
        <InlinePicklist value={sort} label="Sort"
          options={SORTS.map(o => ({ value: o.id, label: o.label }))}
          onCommit={v => setSort(v as AccountsSort)}>
          <span className="k-chip"><span className="k-chip-key">Sort</span>{SORTS.find(o => o.id === sort)!.label}</span>
        </InlinePicklist>
        {((summary?.regions ?? []).length > 0 || region) && (
          <InlinePicklist value={region} label="Region"
            options={[{ value: '', label: 'Any' }, ...(summary?.regions ?? []).map(c => ({ value: c.code, label: `${regionLabel(c.code)} · ${c.accounts}` }))]}
            onCommit={setRegion}>
            <span className="k-chip"><span className="k-chip-key">Region</span>{region ? regionLabel(region) : 'Any'}</span>
          </InlinePicklist>
        )}
        {((summary?.sectors ?? []).length > 0 || sector) && (
          <InlinePicklist value={sector} label="Sector"
            options={[{ value: '', label: 'Any' }, ...(summary?.sectors ?? []).map(c => ({ value: c.code, label: `${sectorLabel(c.code)} · ${c.accounts}` }))]}
            onCommit={setSector}>
            <span className="k-chip"><span className="k-chip-key">Sector</span>{sector ? sectorLabel(sector) : 'Any'}</span>
          </InlinePicklist>
        )}
        <span className="flex-1" />
        {/* Without the S2 facts the health card does not render; S1's Find
            websites stays reachable here, counted the same way (no website). */}
        {records && !hasFacts && pageMissing.length > 0 && (
          <button type="button" className="k-btn-secondary k-btn-secondary--flex" onClick={() => openWebsites(pageMissing)}>
            <Globe size={12} /> Find websites
          </button>
        )}
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search accounts or websites…"
            className="h-8 pl-7 pr-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent w-56" />
        </div>
      </div>

      <div className="k-card">
        <table className="closr-table sales-table w-full">
          <thead>
            <tr>
              {shownColumns.map(col => <th key={col.id} className="text-left">{col.label}</th>)}
              <th aria-label="Actions" style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => {
              const cells = accountCell(a, actions)
              return (
                <tr key={a.id}
                  className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                  data-highlighted={a.id === highlightId ? 'true' : undefined}
                  style={a.id === highlightId ? { background: accent.soft } : undefined}
                  ref={a.id === highlightId ? el => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
                  onClick={() => onSelectAccount(a.id)}>
                  {shownColumns.map(col => <td key={col.id}>{cells[col.id]}</td>)}
                  <td className="cell-actions" onClick={e => e.stopPropagation()}>
                    <AccountRowMenu name={a.name} onOpen={() => onSelectAccount(a.id)} onEdit={records ? () => onSelectAccount(a.id, { edit: true }) : undefined}
                      onMerge={canMerge ? () => openMerge(a) : undefined} />
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={shownColumns.length + 1} className="text-13 text-ink-muted text-center py-8">
                  {/* "No accounts yet" only when the TENANT has none: an empty
                      server view is still a tenant with accounts. */}
                  {(total ?? accounts.length) === 0
                    ? (records ? 'No accounts yet — create one, or add a deal with a company.' : 'No accounts yet.')
                    : q ? 'No accounts match your search.' : chipFiltered ? 'No accounts in this view with that region or sector.' : 'No accounts in this view.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* The honest count line (§8.4): no pagination exists, none is drawn. */}
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-edge-row">
          <span className="text-11 text-ink-muted">
            Showing {filtered.length}{total !== null ? ` of ${total}` : ''} accounts · {SORTS.find(o => o.id === sort)!.footer}{capNote ? ` · ${capNote}` : ''}
          </span>
          <span className="text-11 text-ink-muted">Website addresses are shown as plain domains (www. removed, lower case).</span>
        </div>
      </div>

      {newAccountOpen && records && (
        <NewAccountPopup onClose={() => onNewAccountClose?.()} onChanged={() => reload()}
          onDone={r => {
            if (r.kind === 'opened') { onSelectAccount(r.id); return }
            setHighlightId(r.id)
            if (r.addContactNext) setStakeholdersFor({ accountId: r.id, company: r.name })
          }} />
      )}
      {websiteTargets && records && (
        <AccountWebsitesDrawer accounts={websiteTargets} onClose={() => setWebsiteTargets(null)}
          onSaved={() => reload()} />
      )}
      {mergeFor && (
        <AccountMergeDialog source={mergeFor.source} target={mergeFor.target} onCancel={() => setMergeFor(null)}
          onMerged={(rec, survivorName) => { setMergeFor(null); announceMerge(rec, survivorName, afterMerge); afterMerge() }} />
      )}
      {fixAllOpen && records && (
        <FixAllSheet canMerge={canMerge} onClose={() => setFixAllOpen(false)} onChanged={() => reload()}
          onReviewMerges={list => { setFixAllOpen(false); setMergeQueue(list) }} />
      )}
      {mergeQueue.length > 0 && (
        <AccountMergeDialog key={mergeQueue.length}
          source={{ id: mergedOf(mergeQueue[0]).id, name: mergedOf(mergeQueue[0]).name }}
          target={{ id: survivorOf(mergeQueue[0]).id, name: survivorOf(mergeQueue[0]).name }}
          onCancel={() => setMergeQueue(q => q.slice(1))}
          onMerged={(rec, survivorName) => { setMergeQueue(q => q.slice(1)); announceMerge(rec, survivorName, afterMerge); afterMerge() }} />
      )}
      {stakeholdersFor && records && (
        <NewContactDrawer initialAccount={stakeholdersFor} onClose={() => setStakeholdersFor(null)}
          onCreated={() => { void refreshSalesFigures(); reload() }} />
      )}
    </div>
  )
}
